import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useEditableMarkdown } from '@/shared/EditableMarkdown';
import { MarkdownText } from '@/shared/MarkdownText/MarkdownText';
import { useProductsService } from '@/services/regulatoryWatch';
import type { WorkspaceProductResponse } from '@ditto/shared';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { type FC, useCallback, useEffect, useRef, useState } from 'react';

type ProductListDetailProps = {
  products: WorkspaceProductResponse[];
};

export const ProductListDetail: FC<ProductListDetailProps> = ({ products }) => {
  const { createProduct, updateProduct, deleteProduct } = useProductsService();
  const [selectedId, setSelectedId] = useState<string | null>(
    products[0]?.id ?? null
  );
  const pendingSelectRef = useRef<string | null>(null);

  // Keep selection valid when products change, and auto-select newly created products
  useEffect(() => {
    if (pendingSelectRef.current) {
      const newProduct = products.find(p => p.id === pendingSelectRef.current);
      if (newProduct) {
        setSelectedId(newProduct.id);
        pendingSelectRef.current = null;
        return;
      }
    }
    if (selectedId && !products.find(p => p.id === selectedId)) {
      setSelectedId(products[0]?.id ?? null);
    } else if (!selectedId && products.length > 0) {
      setSelectedId(products[0]!.id);
    }
  }, [products, selectedId]);

  const selected = products.find(p => p.id === selectedId) ?? null;

  const handleAddProduct = useCallback(() => {
    createProduct('New product', '', {
      onSuccess: data => {
        pendingSelectRef.current = data.id;
      },
    });
  }, [createProduct]);

  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium text-neutral-900">All products</span>
      <div className="flex overflow-clip rounded-lg border border-neutral-200">
        {/* Left sidebar — product list */}
        <div className="flex shrink-0 flex-col gap-4 border-r border-neutral-200 bg-neutral-100 pb-4">
          <div className="flex flex-col border-b border-neutral-200">
            {products.map(product => {
              const isSelected = product.id === selectedId;
              return (
                <button
                  key={product.id}
                  type="button"
                  onClick={() => setSelectedId(product.id)}
                  className={`flex w-[200px] items-start border-t border-r border-neutral-200 p-4 text-left ${
                    isSelected
                      ? 'border-l-4 border-l-[#ffe228] bg-white'
                      : 'border-l border-l-neutral-200 bg-neutral-50'
                  }`}
                >
                  <span className="inline-flex min-h-[24px] items-center rounded-lg border border-neutral-200 bg-white px-2 py-[3px] text-xs font-medium text-neutral-900">
                    <span className="line-clamp-1">
                      {product.name || 'Untitled'}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
          <div className="pl-4">
            <Button
              variant="outline"
              size="sm"
              className="h-6 gap-1.5 rounded px-2 text-xs"
              onClick={handleAddProduct}
            >
              <Plus className="size-3.5" />
              Add product
            </Button>
          </div>
        </div>

        {/* Right panel — product editor */}
        <div className="flex flex-1 flex-col gap-4 px-6 py-6">
          {selected ? (
            <ProductEditor
              key={selected.id}
              product={selected}
              onUpdate={updateProduct}
              onDelete={deleteProduct}
              canDelete={products.length > 1}
            />
          ) : (
            <p className="text-sm text-neutral-500">
              Add a product to get started.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Product Editor ──

const ProductEditor: FC<{
  product: WorkspaceProductResponse;
  onUpdate: (
    productId: string,
    body: { name?: string; description?: string }
  ) => void;
  onDelete: (productId: string) => void;
  canDelete: boolean;
}> = ({ product, onUpdate, onDelete, canDelete }) => {
  const [name, setName] = useState(product.name);
  const [description, setDescription] = useState(product.description ?? '');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const save = useCallback(
    (updates: { name?: string; description?: string }) => {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        onUpdate(product.id, updates);
      }, 800);
    },
    [product.id, onUpdate]
  );

  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  const handleDescriptionChange = useCallback(
    (value: string) => {
      setDescription(value);
      save({ description: value });
    },
    [save]
  );

  const { isEditing, textareaProps, onPreviewClick, onPreviewKeyDown } =
    useEditableMarkdown({
      value: description,
      onChange: handleDescriptionChange,
    });

  return (
    <>
      <div className="flex items-end gap-4">
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-neutral-900">
            Product name
          </label>
          <Input
            value={name}
            onChange={e => {
              setName(e.target.value);
              save({ name: e.target.value });
            }}
            className="w-[261px]"
          />
        </div>
        {canDelete && (
          <div className="flex flex-1 justify-end">
            <Button
              variant="ghost"
              size="sm"
              className="h-6 gap-1.5 text-xs text-red-500 hover:text-red-600"
              onClick={() => onDelete(product.id)}
            >
              <Trash2 className="size-3.5" />
              Delete
            </Button>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-neutral-900">
          Product description
        </label>
        {isEditing ? (
          <Textarea
            {...textareaProps}
            className="min-h-[300px] w-full resize-none border-neutral-300 overflow-hidden"
            placeholder="Describe this product..."
          />
        ) : (
          <div
            role="button"
            tabIndex={0}
            onClick={onPreviewClick}
            onKeyDown={onPreviewKeyDown}
            className="group relative min-h-[300px] w-full rounded-lg border border-neutral-200 bg-neutral-50 p-4 cursor-pointer transition-colors hover:border-dashed hover:border-neutral-400 hover:bg-neutral-100/50"
          >
            <Pencil className="absolute top-3 right-3 size-3.5 text-neutral-400 opacity-0 transition-opacity group-hover:opacity-100" />
            {description ? (
              <MarkdownText size="sm">{description}</MarkdownText>
            ) : (
              <span className="text-sm text-neutral-500">
                Click to add a description...
              </span>
            )}
          </div>
        )}
      </div>
    </>
  );
};
